import { intersection } from 'ramda';
import React, { useCallback, useEffect, useReducer } from 'react';
import { IntlProvider } from 'react-intl';
import { backendClientFactory } from '../../apiClient/backend-api/backendClient';
import { customerProfileClientFactory } from '../../apiClient/customer-profile/customerProfileClient';
import { debug } from '../../common/environment-utils';
import translations from '../../component-translations';
import config from '../../config';
import { TrackingProvider } from '../Tracking/TrackingProvider';
import { UserContextErrorPage } from './UserContextError';
import { UserContextErrorCodes } from './UserContextErrorCodes';
import { userContextReducer } from './userContextReducer';
const UserContext = React.createContext(undefined);
const DEFAULT_LOCALE = 'en-GB';
function UserProvider({ children, authProvider, permittedRoles, requiredFeatures, requiredModules, getMe, getFeatures, CustomErrorPage, translationMessages = {}, loadingCallback }) {
    const getMeCall = useCallback(getMe || backendClientFactory(authProvider.getToken).getMe, []);
    const featuresCall = useCallback(getFeatures || customerProfileClientFactory(authProvider.getToken).getFeatures, []);
    const [state, dispatch] = useReducer(userContextReducer, { loading: true, requiredFeatures, permittedRoles, requiredModules });
    useEffect(() => {
        getMeCall()
            .then((foundUser) => dispatch({ type: 'setUser', user: foundUser }))
            .catch(() => dispatch({ type: 'errorCode', errorCode: UserContextErrorCodes.FAILED_ME }));
        featuresCall()
            .then((foundFeatures) => dispatch({ type: 'setFeatures', features: foundFeatures }))
            .catch(() => dispatch({ type: 'errorCode', errorCode: UserContextErrorCodes.FAILED_FEATURES }));
    }, [getMeCall, featuresCall]);
    useEffect(() => {
        if (state.loading === false && loadingCallback) {
            loadingCallback();
        }
    }, [state.loading, loadingCallback]);
    const contextState = {
        features: state.features,
        user: state.user,
        authProvider
    };
    debug('Supported App translations', Object.keys(translationMessages));
    debug('Supported Common translations', Object.keys(translations));
    const getSupportedLanguage = (preferredLanguage) => {
        if (preferredLanguage && translations[preferredLanguage] && translationMessages[preferredLanguage]) {
            return preferredLanguage;
        }
        if (preferredLanguage) {
            const nonRegionalFallback = preferredLanguage.split('-')[0];
            if (translations[nonRegionalFallback] && translationMessages[nonRegionalFallback]) {
                return nonRegionalFallback;
            }
        }
        return null;
    };
    const getCustomerLanguage = () => {
        var _a, _b, _c;
        // Check to see if we have a customer language, and we have definitions for that language
        const customerLanguage = (_c = (_b = (_a = contextState.features) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.core) === null || _c === void 0 ? void 0 : _c.REGION_LANGUAGE;
        debug(`Customer language: ${customerLanguage}`);
        return getSupportedLanguage(customerLanguage);
    };
    const isEnglishVariant = (preferredLanguage) => {
        const englishVariants = ['en-us', 'en-gb', 'en'];
        return englishVariants.includes((preferredLanguage || '').toLowerCase());
    };
    const getUserLanguage = () => {
        // Check to see if we have a user language, and we have definitions for that language
        debug(`User language: ${navigator.language}`);
        return getSupportedLanguage(navigator.language);
    };
    if (state.loading) {
        return (React.createElement(React.Fragment, null));
    }
    debug('Supported Languages', intersection(Object.keys(translations), Object.keys(translationMessages)));
    const supportedUserLanguage = getUserLanguage();
    const supportedCustomerLanguage = getCustomerLanguage();
    let selectedLanguage = supportedUserLanguage || supportedCustomerLanguage || DEFAULT_LOCALE;
    /*
      If the customer is an English variant, use the customer profile setting.
      This was because peoples browsers arent always setup properly in English.
      This needs to be rolled back when:
      1) when we need to support American people working at an British company.
      2) when we need to support British people working at an American company.
    */
    if (isEnglishVariant(supportedUserLanguage) && isEnglishVariant(supportedCustomerLanguage)) {
        selectedLanguage = supportedCustomerLanguage;
    }
    debug(`Chosen language: ${selectedLanguage}`);
    const commonMessages = translations[selectedLanguage];
    const appMessages = translationMessages[selectedLanguage];
    const allTranslations = Object.assign(Object.assign({}, commonMessages), appMessages);
    if (state.errorCode) {
        return (React.createElement(IntlProvider, { locale: selectedLanguage, defaultLocale: DEFAULT_LOCALE, messages: allTranslations },
            React.createElement(UserContextErrorPage, { CustomErrorPage: CustomErrorPage, errorCode: state.errorCode })));
    }
    return (React.createElement(UserContext.Provider, { value: contextState },
        React.createElement(IntlProvider, { locale: selectedLanguage, defaultLocale: DEFAULT_LOCALE, messages: allTranslations },
            React.createElement(TrackingProvider, { features: state.features, user: state.user, forceEnablementForEnvironment: config.tracking.forceEnabled === 'true' }, children))));
}
function useUserState() {
    const context = React.useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserState must be used within a UserProvider');
    }
    return context;
}
export { UserProvider, useUserState };
//# sourceMappingURL=UserContext.js.map